import RGL from 'react-grid-layout';
import { useForm } from 'react-hook-form';

import { Dashboard } from 'types/dashboard';
import { SystemStateWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { useModal } from 'hooks/useModal';
import { useDashboard } from 'dashboards/hooks/useDashboard';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type SystemStateWidgetFieldValues = { name: string };

type SystemStateFormProps = {
  widget?: SystemStateWidget;
  dashboardId: string;
};

export function SystemStateForm({ widget, dashboardId }: SystemStateFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess(data) {
      resetForm(data);
      close();
    },
  });

  const {
    formState: { errors, dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<SystemStateWidgetFieldValues>({
    defaultValues: { name: widget?.name },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  function resetForm(updatedDashboard: Dashboard) {
    if (!widget?.id) {
      return;
    }

    const updatedWidget = updatedDashboard.configuration.widgets.find(
      ({ id }) => id === widget.id
    ) as SystemStateWidget;

    if (!updatedWidget) {
      return;
    }

    reset({ name: updatedWidget.name });
  }

  function onSubmit({ name }: SystemStateWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();

    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 6, 4);

    const updatedDashboard = upsertWidget(
      dashboard,
      new SystemStateWidget(id, name, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="system-state" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
